import React, { useEffect, useState } from 'react';

import BootstrapSelect from '../bootstrap-select/BootstrapSelect';
import { Map as ImmutableMap } from 'immutable';
import { locales } from '../locales';

const LanguageSelect = ({ value, onChange, className = '', isValid, isDefaultOptionSelectable, allowedLocales }) => {
  const defaultText = isDefaultOptionSelectable ? 'Default' : '';

  const [genericLanguage, setGenericLanguage] = useState(defaultText);
  const [showSpecificLanguage, setShowSpecificLanguage] = useState(false);
  const [specificLanguageMap, setSpecificLanguageMap] = useState(new ImmutableMap());
  const [specificLanguage, setSpecificLanguage] = useState('');
  const [usedLocales, setUsedLocales] = useState(locales);

  const displayLocale = (localeCode, localeText) => {
    return localeText + ' (' + localeCode + ')';
  };

  useEffect(() => {
    if (!allowedLocales || allowedLocales.length === 0) {
      setUsedLocales(locales);
      return;
    }
    const allowedLanguagesSet = new Set();
    const allowedLocalesSet = new Set();
    for (const locale of allowedLocales) {
      allowedLocalesSet.add(locale);
      if (locale.includes('-')) {
        allowedLanguagesSet.add(locale.split('-')[0]);
      }
    }

    const filteredLocales = locales.filter((value, key) => allowedLanguagesSet.has(key.split('-')[0]));
    const localesToUse = new Map();
    for (const [language, languageData] of filteredLocales.entries()) {
      const languageDataObj = {};
      languageDataObj.name = languageData.name;
      if (languageData.locales) {
        const filteredLocales = languageData.locales.filter((value, key) => allowedLocalesSet.has(key));
        languageDataObj.locales = filteredLocales;
        languageDataObj.defaultLocale = filteredLocales.keySeq().first();
      }
      localesToUse.set(language, languageDataObj);
    }

    setUsedLocales(new ImmutableMap(localesToUse).sortBy((v, k) => k));
    setShowSpecificLanguage(false);
  }, [allowedLocales]);

  useEffect(() => {
    for (const [language, languageData] of usedLocales) {
      if (language === value || value === defaultText) {
        setGenericLanguage(value);
        setShowSpecificLanguage(false);
        return;
      }
      if (languageData.locales) {
        for (const [locale] of languageData.locales) {
          if (locale === value) {
            setGenericLanguage(language);
            setShowSpecificLanguage(true);
            setSpecificLanguageMap(languageData.locales);
            setSpecificLanguage(value);
            return;
          }
        }
      }
    }
  }, [value, defaultText, usedLocales]);

  const processGenericLanguage = locale => {
    const specificLocales = usedLocales.get(locale)?.locales;
    if (specificLocales) {
      const defaultLocale = usedLocales.get(locale).defaultLocale;

      setSpecificLanguageMap(specificLocales);
      setGenericLanguage(locale);
      setSpecificLanguage(defaultLocale);
      setShowSpecificLanguage(true);
      onChange(defaultLocale);
    } else {
      setGenericLanguage(locale);
      setShowSpecificLanguage(false);
      onChange(locale);
    }
  };

  const processSpecificLanguage = locale => {
    setSpecificLanguage(locale);
    onChange(locale);
  };

  return (
    <div className={className}>
      <div className="mt-1 mb-1">
        <BootstrapSelect
          is-valid={isValid}
          el-width="100%"
          data-size="5"
          data-live-search="true"
          value={genericLanguage}
          onChange={e => processGenericLanguage(e.target.value)}
        >
          <option value={defaultText} hidden={!isDefaultOptionSelectable}>
            {defaultText}
          </option>
          {usedLocales.map((value, key) => {
            return (
              <option value={key} key={key}>
                {displayLocale(key, value.name)}
              </option>
            );
          })}
        </BootstrapSelect>
      </div>
      <div className="mt-1 mb-1" hidden={!showSpecificLanguage}>
        <BootstrapSelect
          is-valid={isValid}
          el-width="100%"
          data-size="5"
          data-live-search="true"
          value={specificLanguage}
          onChange={e => processSpecificLanguage(e.target.value)}
        >
          {specificLanguageMap
            .keySeq()
            .sort()
            .map((value, index) => {
              return (
                <option value={value} key={value}>
                  {displayLocale(value, specificLanguageMap.get(value))}
                </option>
              );
            })}
        </BootstrapSelect>
      </div>
    </div>
  );
};

export default LanguageSelect;
